import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseHoldings, SAMPLE_HOLDINGS_TEXT } from '../utils/holdingsParser';
import { extractHoldingsFromImage, holdingsToText } from '../utils/ocrHoldings';
import { useCurrency } from '../contexts/CurrencyContext';
import { track } from '../utils/analytics';
import './FirstRun.css';

// The first minute. A signed-in reader with no holdings sees this instead of
// an empty note: pick a currency, paste holdings (or read them off a
// screenshot), and the note builds itself. GuideStrip then explains the note
// once, and the checklist nudges the two things that unlock more of it.

export function FirstRun({ userProfile, onSave, onSaved }) {
  const navigate = useNavigate();
  const { setCurrency } = useCurrency();
  const [base, setBase] = useState(userProfile?.currency || 'USD');
  const [text, setText] = useState('');
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ocr, setOcr] = useState(null);

  const readScreenshot = async (file) => {
    if (!file) return;
    setOcr('Reading the screenshot on this device…');
    try {
      const { holdings } = await extractHoldingsFromImage(file, (p) => setOcr(`Reading… ${Math.round(p * 100)}%`));
      if (!holdings.length) { setOcr('No tickers with share counts were found. Try a tighter crop of the holdings table.'); return; }
      setText(holdingsToText(holdings));
      setErrors([]);
      setOcr(`Read ${holdings.length} holdings. Nothing was uploaded. Check them, then build the note.`);
      track('first_run_screenshot', { holdings: holdings.length });
    } catch (err) {
      setOcr(err.message || 'Could not read that image.');
    }
  };

  const submit = async (e, raw) => {
    if (e) e.preventDefault();
    const source = raw || text;
    const { holdings, errors: parseErrors } = parseHoldings(source);
    if (parseErrors.length) { setErrors(parseErrors); return; }
    if (!holdings.length) { setErrors(['Add at least one holding, like "VTI 120".']); return; }
    setErrors([]);
    setBusy(true);
    try {
      setCurrency(base);
      await onSave({
        ...(userProfile || {}),
        investment_goals: userProfile?.investment_goals || [],
        preferred_sectors: userProfile?.preferred_sectors || [],
        currency: base,
        holdings: holdings.map((h) => ({ ticker: h.ticker, quantity: String(h.quantity), avgPrice: '' })),
      });
      track('first_run_saved', { holdings: holdings.length, currency: base, sample: raw === SAMPLE_HOLDINGS_TEXT });
      if (onSaved) onSaved();
    } catch (err) {
      setErrors(['Could not save your holdings. Try again in a moment.']);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fr">
      <div className="dd-eyebrow">First run</div>
      <h1 className="dd-serif">Let's build your note.</h1>
      <p className="fr-lead">Tell it what you hold. Everything on this page, the risk figures, the frontier, the analyst's answers, is computed from that list. It takes about a minute.</p>

      <form className="fr-form" onSubmit={submit}>
        <div className="fr-step">
          <div className="fr-num dd-mono">1</div>
          <div>
            <div className="fr-label">Money in</div>
            <div className="fr-seg" role="group" aria-label="Base currency">
              <button type="button" className={base === 'USD' ? 'on' : ''} onClick={() => setBase('USD')}>$ US dollars</button>
              <button type="button" className={base === 'INR' ? 'on' : ''} onClick={() => setBase('INR')}>₹ Indian rupees</button>
            </div>
            <div className="fr-hint dd-muted">Totals are shown in this currency. Each holding keeps its own price. NSE stocks take a .NS suffix, like RELIANCE.NS.</div>
          </div>
        </div>

        <div className="fr-step">
          <div className="fr-num dd-mono">2</div>
          <div className="fr-grow">
            <div className="fr-label">What you hold</div>
            <textarea
              className="fr-text dd-mono"
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={base === 'INR' ? 'RELIANCE.NS 40, TCS.NS 15, INFY.NS 60' : SAMPLE_HOLDINGS_TEXT}
              aria-label="Holdings, ticker and share count"
            />
            <div className="fr-tools">
              <label className="fr-import">
                Read from a screenshot
                <input type="file" accept="image/*" hidden onChange={(e) => { readScreenshot(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
              <button type="button" className="pn-textbtn" onClick={() => submit(null, SAMPLE_HOLDINGS_TEXT)} disabled={busy}>or use a sample portfolio</button>
            </div>
            {ocr && <div className="fr-hint dd-mono dd-muted">{ocr}</div>}
            {errors.length > 0 && <ul className="fr-errors" role="alert">{errors.map((er) => <li key={er}>{er}</li>)}</ul>}
          </div>
        </div>

        <div className="fr-step">
          <div className="fr-num dd-mono">3</div>
          <div>
            <button type="submit" className="dd-btn fr-go" disabled={busy}>{busy ? 'Saving…' : 'Build my note'}</button>
            <div className="fr-hint dd-muted">Average cost, goals and risk tolerance can come later, in <button type="button" className="pn-textbtn pn-textbtn--inline" onClick={() => navigate('/settings')}>Holdings &amp; profile</button>.</div>
          </div>
        </div>
      </form>
    </div>
  );
}

const GUIDE = [
  ['Summary', 'Your total in your currency, today\'s estimated move, and a short briefing written against your names.'],
  ['Risk', 'Sharpe is return per unit of risk; above 1 is good. Beta is how hard you move with the market. VaR is a plausible bad day in money.'],
  ['Frontier', 'The dot is you. The line is every mix of your holdings that gives the best return for its risk. The dashed line is the move the optimiser suggests.'],
  ['Lab', 'Try a different mix, replay past selloffs on your book, and see whether last year\'s optimiser advice would have worked.'],
  ['Ask the analyst', 'Questions are answered from your holdings and live prices, verdict first, with the numbers under it.'],
];

export function GuideStrip({ user, userProfile, holdings = [] }) {
  const navigate = useNavigate();
  const key = `dd_guide_done_${user?.uid || 'anon'}`;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(key) !== '1'; } catch (e) { return true; }
  });
  useEffect(() => {
    try { if (!open) localStorage.setItem(key, '1'); } catch (e) { /* ignore */ }
  }, [open, key]);

  const noCost = holdings.length > 0 && holdings.every((h) => !(Number(h.avgPrice) > 0));
  const noProfile = !userProfile?.risk_tolerance && !(userProfile?.investment_goals?.length);
  const todo = [
    noCost && ['Add your average cost', 'so the note can show gains since you bought, not just since today.'],
    noProfile && ['Set your risk tolerance and horizon', 'to unlock the health review and better answers from the analyst.'],
  ].filter(Boolean);

  if (!open) return null;
  return (
    <div className="fr-guide" role="region" aria-label="How to read this note">
      <div className="fr-guide-head">
        <div className="dd-eyebrow">How to read this note</div>
        <button type="button" className="pn-textbtn" onClick={() => setOpen(false)}>Got it, hide this</button>
      </div>
      <ol className="fr-guide-list">
        {GUIDE.map(([title, body]) => <li key={title}><strong>{title}.</strong> {body}</li>)}
      </ol>
      {todo.length > 0 && (
        <div className="fr-todo">
          {todo.map(([title, body]) => (
            <button key={title} type="button" className="fr-todo-item" onClick={() => navigate('/settings')}>
              <span className="fr-todo-box" aria-hidden="true" />
              <span><strong>{title}</strong> <span className="dd-muted">{body}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default FirstRun;
